import { Button, Form, Input, InputNumber, Select } from 'antd';
import React, { FC, useEffect } from 'react';

import { openNotification } from '../../../common/helpers';
import { NotificationTypes } from '../../../common/interfaces';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import { ordersApi } from '../../../store/api/orders.api';
import { types } from '../Orders.constants';
import { formatSum, parseStringToNumber } from '../Orders.helpers';
import { AssetType, Order, OrderStatus, OrderType } from '../Orders.interfaces';
import styles from './FormOrder.module.scss';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onGetOrders: () => void;
  dataOrder?: Order;
};

const { Option } = Select;

enum SubmitType {
  DRAFT = 'draft',
  ORDER = 'order',
}

export const FormOrder: FC<Props> = ({ onClose, onGetOrders, dataOrder }) => {
  const { currency, virtualAssets } = useTypedSelector(
    (state) => state.registration,
  );

  const [createOrder, { isLoading: loadingCreateOrders }] =
    ordersApi.useCreateOrdersMutation();

  const [sentVerifyCode, { isLoading: loadingVerifyCode }] =
    ordersApi.useSentVerifyCodeMutation();

  // OrderType.BUY - AssetType.CURRENCY - currencyId - corrCurrencyId - sum
  // OrderType.SELL - AssetType.CURRENCY - currencyId - corrCurrencyId - sum
  // OrderType.BUY - AssetType.VIRTUAL_ASSET  - currencyId - virtualAssetId - sum
  // OrderType.SELL - AssetType.VIRTUAL_ASSET  - currencyId - virtualAssetId - sum
  // OrderType.INCOME - AssetType.CURRENCY  - currencyId - recipientBank - sum
  // OrderType.OUTCOME - AssetType.CURRENCY  - currencyId - sum

  const [initialValues, setInitialValues] = React.useState<Order>({
    status: OrderStatus.IN_WAITING_VIEW,
    orderType: OrderType.BUY,
    assetType: AssetType.CURRENCY,
    currencyId: currency[0].value,
    corrCurrencyId: currency[1].value,
    curs: 0,
    currPair: '',
    sum: '0.00',
    comment: '',
  });

  const readOnly = dataOrder ? dataOrder?.status !== OrderStatus.DRAFT : false;

  const [form] = Form.useForm();
  const onSubmit = async (type: SubmitType) => {
    const sum = form.getFieldValue('sum');

    let valuesForm = form.getFieldsValue();

    valuesForm = {
      ...valuesForm,

      // Обновляем статус заявки на DRAFT т.к. без verifyCode можно отправить только в черновик
      status: OrderStatus.DRAFT,
    };

    // В ордере фигурирует сумма
    const isOrderSum = 'sum' in initialValues && sum && sum !== '0.00';

    // В ордере фигурирует кол-во
    const isOrderQuantity = 'quantity' in initialValues && valuesForm.quantity;

    if (isOrderSum || isOrderQuantity) {
      if (isOrderSum) {
        // Если сумма не пустая, то парсим ее в число и заменяем в объекте
        valuesForm = {
          ...valuesForm,
          sum: parseStringToNumber(sum),
        };
      }

      // Если просто сохраняем в черновик
      if (type === SubmitType.DRAFT) {
        try {
          await createOrder(valuesForm).unwrap();
          openNotification(
            NotificationTypes.SUCCESS,
            'Заявка успешно сохранена в черновик',
          );
          onGetOrders();
          onClose();
          form.resetFields();
        } catch (error) {
          openNotification(
            NotificationTypes.ERROR,
            'Произошла ошибка при создании заявки',
          );
        }
      }

      // Если хотим отправить на рассмотрение
      if (type === SubmitType.ORDER) {
        // Если есть код верификации с почты
        if ('verifyCode' in valuesForm) {
          valuesForm = {
            ...valuesForm,
            status: OrderStatus.IN_WAITING_VIEW,
            orderId: initialValues.orderId,
          };
          try {
            await createOrder(valuesForm).unwrap();
            openNotification(
              NotificationTypes.SUCCESS,
              'Заявка успешно создана',
            );
            onGetOrders();
            onClose();
            form.resetFields();
          } catch (error) {
            openNotification(
              NotificationTypes.ERROR,
              'Произошла ошибка при создании заявки',
            );
          }
        } else {
          // Если хотим отправить, но кода еще нет
          try {
            const res = await createOrder(valuesForm).unwrap();
            // Отправляем запрос на получение кода верификации
            await sentVerifyCode({ orderId: res.orderId });
            setInitialValues({
              ...initialValues,
              verifyCode: '',
              orderId: res.orderId,
            });
          } catch (error) {
            openNotification(
              NotificationTypes.ERROR,
              'Произошла ошибка при создании заявки',
            );
          }
        }
      }
    }

    // }
  };

  const handleChangeOrderType = (value: OrderType) => {
    if ([OrderType.INCOME, OrderType.OUTCOME].includes(value)) {
      const updatedInitialValues: Order = {
        orderType: value,
        assetType: AssetType.CURRENCY,
        status: initialValues.status,
        currencyId: currency[0].value,
        sum: initialValues.sum,
        comment: initialValues.comment,
      };

      if (value === OrderType.INCOME) {
        updatedInitialValues.recipientBank = '';
      }

      setInitialValues(updatedInitialValues);
      form.setFieldsValue(updatedInitialValues);
    }

    if ([OrderType.BUY, OrderType.SELL].includes(value)) {
      // Если "покупка" - поле "Кол-во" меняется на "Сумма"
      const updatedInitialValues: Order = {
        orderType: value,
        assetType: AssetType.CURRENCY,
        status: initialValues.status,
        corrCurrencyId: currency[1].value,
        currencyId: currency[0].value,
        sum: initialValues.sum,
        comment: initialValues.comment,
      };

      setInitialValues(updatedInitialValues);
      form.setFieldsValue(updatedInitialValues);
    }
  };

  const handleChangeAssetType = (value: AssetType) => {
    if (initialValues.orderType && initialValues.orderType == OrderType.BUY) {
      if (value === AssetType.CURRENCY) {
        const updatedInitialValues: Order = {
          orderType: initialValues.orderType,
          assetType: value,
          status: initialValues.status,
          corrCurrencyId: currency[1].value,
          currencyId: currency[0].value,
          sum: initialValues.sum,
          comment: initialValues.comment,
        };

        setInitialValues(updatedInitialValues);
        form.setFieldsValue(updatedInitialValues);
      }

      if (value === AssetType.VIRTUAL_ASSET) {
        const updatedInitialValues: Order = {
          orderType: initialValues.orderType,
          assetType: value,
          status: initialValues.status,
          corrCurrencyId: currency[0].value,
          virtualAssetId: virtualAssets[0].value,
          sum: initialValues.sum,
          comment: initialValues.comment,
        };

        setInitialValues(updatedInitialValues);
        form.setFieldsValue(updatedInitialValues);
      }

      if (value === AssetType.SHARE) {
        const updatedInitialValues: Order = {
          orderType: initialValues.orderType,
          assetType: value,
          status: initialValues.status,
          quantity: '1',
          shareName: '',
          corrCurrencyId: currency[1].value,
          comment: initialValues.comment,
        };

        setInitialValues(updatedInitialValues);
        form.setFieldsValue(updatedInitialValues);
      }
    }

    if (initialValues.orderType && initialValues.orderType == OrderType.SELL) {
      if (value === AssetType.CURRENCY) {
        const updatedInitialValues: Order = {
          orderType: initialValues.orderType,
          assetType: value,
          status: initialValues.status,
          corrCurrencyId: currency[1].value,
          currencyId: currency[0].value,
          quantity: initialValues.quantity,
          comment: initialValues.comment,
        };

        setInitialValues(updatedInitialValues);
        form.setFieldsValue(updatedInitialValues);
      }

      if (value === AssetType.VIRTUAL_ASSET) {
        const updatedInitialValues: Order = {
          orderType: initialValues.orderType,
          assetType: value,
          status: initialValues.status,
          corrCurrencyId: currency[0].value,
          virtualAssetId: virtualAssets[0].value,
          quantity: initialValues.quantity,
          comment: initialValues.comment,
        };

        setInitialValues(updatedInitialValues);
        form.setFieldsValue(updatedInitialValues);
      }

      if (value === AssetType.SHARE) {
        const updatedInitialValues: Order = {
          orderType: initialValues.orderType,
          assetType: value,
          status: initialValues.status,
          quantity: '1',
          shareName: '',
          corrCurrencyId: currency[1].value,
          comment: initialValues.comment,
        };

        setInitialValues(updatedInitialValues);
        form.setFieldsValue(updatedInitialValues);
      }
    }

    if (
      initialValues.orderType &&
      [OrderType.INCOME, OrderType.OUTCOME].includes(initialValues.orderType)
    ) {
      if (value === AssetType.SHARE) {
        const updatedInitialValues: Order = {
          orderType: initialValues.orderType,
          assetType: value,
          status: initialValues.status,
          quantity: '1',
          shareName: '',
          depositoryName: '',
          personalAccountNumber: '',
          depoAccount: '',
          invoiceSection: '',
          indetifier: '',
          depoAccountHolder: '',
          numberAndDateAgreement: '',
          comment: initialValues.comment,
        };
        setInitialValues(updatedInitialValues);
        form.setFieldsValue(updatedInitialValues);
      }

      if (value === AssetType.CURRENCY) {
        const updatedInitialValues: Order = {
          orderType: initialValues.orderType,
          assetType: value,
          status: initialValues.status,
          corrCurrencyId: currency[1].value,
          currencyId: currency[0].value,
          sum: initialValues.sum,
          comment: initialValues.comment,
        };

        setInitialValues(updatedInitialValues);
        form.setFieldsValue(updatedInitialValues);
      }
    }
  };

  const handleBlurSum = (fieldName: string) => () => {
    const value = form.getFieldValue(fieldName); // Получаем текущее значение поля
    let number = parseFloat(value);

    if (isNaN(number)) {
      number = 0;
    }

    if (value) {
      form.setFieldValue(fieldName, formatSum(number.toString())); // Устанавливаем форматированное значение
    }
  };

  const onCloseModal = () => {
    onClose();
    form.resetFields();
  };

  useEffect(() => {
    if (dataOrder) {
      // Форматируем приходящую сумму, так как приходит number (без форматирования)
      const updateDataOrder = {
        ...dataOrder,
        sum: formatSum((dataOrder.sum ?? 0).toString()),
      };
      setInitialValues(updateDataOrder);
      form.setFieldsValue(updateDataOrder);
    }

    return () => form.resetFields();
  }, [dataOrder, form]);
  return (
    <Form
      layout="vertical"
      initialValues={initialValues}
      className={styles.container}
      form={form}
    >
      {/* Инпуты формы, будут меняться в зависимости от выбранных assetType и orderType  */}

      <div className={styles['form-item']}>
        <Form.Item
          layout="vertical"
          label="Тип заявки"
          className={styles['form-item__type']}
          name={'orderType'}
        >
          <Select
            disabled={readOnly}
            onChange={handleChangeOrderType}
            size="large"
            options={types}
            placeholder="Выберете тип заявки"
          />
        </Form.Item>
      </div>

      <div className={styles['form-item']}>
        <Form.Item
          layout="vertical"
          label="Актив"
          className={styles['form-item__asset']}
          name={'assetType'}
        >
          <Select
            disabled={readOnly}
            onChange={handleChangeAssetType}
            size="large"
            placeholder="Выберете актив"
          >
            <Option value={AssetType.CURRENCY}>Валюта</Option>

            <Option
              value={AssetType.VIRTUAL_ASSET}
              disabled={[OrderType.INCOME, OrderType.OUTCOME].includes(
                initialValues.orderType!,
              )}
            >
              Виртуальный актив
            </Option>

            {/* <Option value={AssetType.SHARE}>Ценная бумага</Option> */}
          </Select>
        </Form.Item>
      </div>

      <div className={styles['form-item__numbers']}>
        {'shareName' in initialValues && (
          <Form.Item
            label="Наименование акции"
            className={styles['form-item']}
            name="shareName"
          >
            <Input
              readOnly={readOnly}
              size="large"
              placeholder="Наименование акции"
            />
          </Form.Item>
        )}

        {'virtualAssetId' in initialValues && (
          <Form.Item
            label="Виртуальный актив"
            className={styles['form-item__asset']}
            name="virtualAssetId"
          >
            <Select
              disabled={readOnly}
              size="large"
              filterSort={(optionA, optionB) =>
                (optionA?.label ?? '')
                  .toLowerCase()
                  .localeCompare((optionB?.label ?? '').toLowerCase())
              }
              options={virtualAssets}
              placeholder="Виртуальный актив"
            />
          </Form.Item>
        )}

        {'currencyId' in initialValues && (
          <Form.Item
            label="Валюта"
            className={styles['form-item']}
            name="currencyId"
          >
            <Select
              disabled={readOnly}
              size="large"
              options={currency}
              placeholder="Валюта"
            />
          </Form.Item>
        )}

        {'sum' in initialValues && (
          <div className={styles['form-item']}>
            <Form.Item
              rules={[
                { required: false, message: 'Введите сумму' },
                {
                  validator: (_, value) => {
                    // Удаляем ненужные символы
                    const normalizedValue = value
                      .replace(/-/g, '')
                      .replace(/\s/g, '')
                      .replace(',', '.');

                    if (!normalizedValue) {
                      return Promise.reject(
                        new Error('Поле не может быть пустым'),
                      );
                    }

                    const number = parseFloat(normalizedValue);

                    if (isNaN(number)) {
                      return Promise.reject(
                        new Error('Введите корректное число'),
                      );
                    }

                    if (number === 0) {
                      return Promise.reject(
                        new Error('Значение не может быть 0.00'),
                      );
                    }

                    if (number < 0) {
                      return Promise.reject(
                        new Error('Значение не может быть отрицательным'),
                      );
                    }

                    if (number > 1000000) {
                      return Promise.reject(
                        new Error('Значение не может превышать 1,000,000'),
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
              label={`Сумма ${initialValues.orderType === OrderType.SELL ? 'продажи' : initialValues.orderType === OrderType.BUY ? 'покупки' : ''}`}
              className={styles['form-item']}
              name="sum"
            >
              <Input
                onBlur={handleBlurSum('sum')}
                readOnly={readOnly}
                size="large"
                placeholder="Сумма"
              />
            </Form.Item>
          </div>
        )}

        {'quantity' in initialValues &&
          (Number(initialValues.quantity) !== 0 ||
            dataOrder?.status === OrderStatus.DRAFT) && (
            <div className={styles['form-item']}>
              <Form.Item
                label="Количество"
                className={styles['form-item']}
                name="quantity"
                rules={[
                  { required: true, message: 'Введите кол-во' },
                  {
                    validator: (_, value) => {
                      if (value <= 0) {
                        return Promise.reject(new Error('Неверное значение'));
                      }
                    },
                  },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  readOnly={readOnly}
                  size="large"
                  placeholder="Кол-во"
                />
              </Form.Item>
            </div>
          )}
      </div>

      {'corrCurrencyId' in initialValues && (
        <div className={styles['form-item']}>
          <Form.Item
            label={`Валюта ${initialValues.orderType === OrderType.SELL ? 'покупки' : 'продажи'}`}
            className={styles['form-item']}
            name="corrCurrencyId"
          >
            <Select
              disabled={readOnly}
              size="large"
              options={currency}
              placeholder={`Валюта ${initialValues.orderType === OrderType.SELL ? 'покупки' : 'продажи'}`}
            />
          </Form.Item>
        </div>
      )}

      {'curs' in initialValues && dataOrder?.status === OrderStatus.SUCCESS && (
        <div className={styles['form-item']}>
          <Form.Item label="Курс" className={styles['form-item']} name="curs">
            <Input
              readOnly={readOnly}
              size="large"

              // placeholder="Депозитарный счет"
            />
          </Form.Item>
        </div>
      )}

      {'recipientBank' in initialValues && (
        <div className={styles['form-item']}>
          <Form.Item
            label="Банк"
            className={styles['form-item']}
            name="recipientBank"
          >
            <Input
              readOnly={readOnly}
              size="large"
              placeholder="Реквизиты банка"
            />
          </Form.Item>
        </div>
      )}

      {'depositoryName' in initialValues && (
        <div className={styles['form-item']}>
          <Form.Item
            label="Наименование депозитария"
            className={styles['form-item']}
            name="depositoryName"
          >
            <Input
              readOnly={readOnly}
              size="large"
              placeholder="Наименование депозитария"
            />
          </Form.Item>
        </div>
      )}

      {'personalAccountNumber' in initialValues && (
        <div className={styles['form-item']}>
          <Form.Item
            label="Номер личного счета"
            className={styles['form-item']}
            name="personalAccountNumber"
          >
            <Input
              readOnly={readOnly}
              size="large"
              placeholder="Номер личного счета"
            />
          </Form.Item>
        </div>
      )}

      {'depoAccount' in initialValues && (
        <div className={styles['form-item']}>
          <Form.Item
            label="Депозитарный счет"
            className={styles['form-item']}
            name="depoAccount"
          >
            <Input
              readOnly={readOnly}
              size="large"
              placeholder="Депозитарный счет"
            />
          </Form.Item>
        </div>
      )}

      {'invoiceSection' in initialValues && (
        <div className={styles['form-item']}>
          <Form.Item
            label="Раздел счета"
            className={styles['form-item']}
            name="invoiceSection"
          >
            <Input
              readOnly={readOnly}
              size="large"
              placeholder="Раздел счета"
            />
          </Form.Item>
        </div>
      )}

      {'indetifier' in initialValues && (
        <div className={styles['form-item']}>
          <Form.Item
            label="Идентификатор"
            className={styles['form-item']}
            name="indetifier"
          >
            <Input
              readOnly={readOnly}
              size="large"
              placeholder="Идентификатор"
            />
          </Form.Item>
        </div>
      )}

      {'depoAccountHolder' in initialValues && (
        <div className={styles['form-item']}>
          <Form.Item
            label="Владелец депозитарного счета"
            className={styles['form-item']}
            name="depoAccountHolder"
          >
            <Input
              readOnly={readOnly}
              size="large"
              placeholder="Владелец депозитарного счета"
            />
          </Form.Item>
        </div>
      )}

      {'numberAndDateAgreement' in initialValues && (
        <div className={styles['form-item']}>
          <Form.Item
            label="Номер и дата соглашения"
            className={styles['form-item']}
            name="numberAndDateAgreement"
          >
            <Input
              readOnly={readOnly}
              size="large"
              placeholder="Номер и дата соглашения"
            />
          </Form.Item>
        </div>
      )}

      <div className={styles['form-item']}>
        <Form.Item
          label="Комментарий"
          className={styles['form-item']}
          name="comment"
        >
          <Input.TextArea
            readOnly={readOnly}
            size="large"
            placeholder="Комментарий"
          />
        </Form.Item>
      </div>

      {'verifyCode' in initialValues && (
        <div className={styles['form-item']}>
          <Form.Item
            required
            label={
              <span className={styles['form-item__verify__label']}>
                Введите код подтверждения
              </span>
            }
            className={styles['form-item__verify']}
            name="verifyCode"
          >
            <Input type="number" readOnly={readOnly} size="large" />
          </Form.Item>
        </div>
      )}

      <div className={styles.buttons}>
        {/* Если создаем новую заявку */}
        {!dataOrder && (
          <div>
            <Button
              htmlType="submit"
              size="large"
              color="primary"
              variant="dashed"
              key="back"
              onClick={() => onSubmit(SubmitType.DRAFT)}
              loading={loadingCreateOrders}
            >
              Сохранить черновик
            </Button>
          </div>
        )}

        <div>
          <Button
            htmlType="button"
            size="large"
            key="back"
            onClick={onCloseModal}
          >
            Отмена
          </Button>

          {/* Если у заявка в статусе DRAFT или новая заявка */}
          {(dataOrder?.status === OrderStatus.DRAFT || !dataOrder) && (
            <Button
              size="large"
              key="submit"
              type="primary"
              htmlType="submit"
              loading={loadingCreateOrders || loadingVerifyCode}
              onClick={() => onSubmit(SubmitType.ORDER)}
            >
              Подписать и отправить
            </Button>
          )}
        </div>
      </div>
    </Form>
  );
};
